import React, { useState, useEffect, useCallback } from "react";
import { api, useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui-kit";
import { CreditCard, ArrowsClockwise, XCircle, GooglePlayLogo } from "@phosphor-icons/react";

const fmtDate = (iso, lang) => {
  if (!iso) return "?";
  try {
    return new Date(iso).toLocaleDateString(lang === "cs" ? "cs-CZ" : "en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso.slice(0, 10); }
};

export const SubscriptionCard = ({ lang }) => {
  const { refreshUser } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // "cancel" | "change" | null

  const load = useCallback(() => {
    api.get("/billing/subscription")
      .then(({ data }) => setSub(data))
      .catch(() => setSub(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      setConfirm(null);
      load();
      refreshUser?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "cs" ? "Akce se nezdařila" : "Action failed"));
    } finally { setBusy(false); }
  };

  if (loading || !sub || sub.source === "none") return null;

  const cs = lang === "cs";
  const targetPlan = sub.plan === "ELITE" ? "PREMIUM" : "ELITE";
  const isUpgrade = targetPlan === "ELITE";
  const targetPrice = targetPlan === "ELITE" ? 1290 : 499;

  return (
    <Card className="p-6 space-y-4" data-testid="subscription-card">
      <h3 className="text-lg font-medium text-zinc-100 flex items-center gap-2">
        <CreditCard weight="bold" size={18} className="text-blue-400" />
        {cs ? "Moje předplatné" : "My subscription"}
      </h3>

      {sub.source === "google_play" ? (
        <div className="space-y-3">
          <div className="text-sm text-zinc-300">
            {cs ? `Plán ${sub.plan} — aktivní do ${fmtDate(sub.expires_at, lang)}` : `${sub.plan} plan — active until ${fmtDate(sub.expires_at, lang)}`}
          </div>
          <div className="flex items-start gap-2 text-sm text-zinc-400">
            <GooglePlayLogo weight="bold" size={16} className="mt-0.5 flex-shrink-0" />
            <span>{cs ? "Předplatné spravujete v Google Play." : "Your subscription is managed in Google Play."}</span>
          </div>
          <a href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noreferrer" data-testid="subscription-gplay-link" className="inline-block text-sm text-blue-400 hover:underline">
            {cs ? "Otevřít předplatná v Google Play →" : "Open Google Play subscriptions →"}
          </a>
        </div>
      ) : !sub.manageable ? (
        <div className="text-sm text-zinc-300" data-testid="subscription-info">
          {cs ? `Plán ${sub.plan} — aktivní do ${fmtDate(sub.expires_at || sub.current_period_end, lang)}` : `${sub.plan} plan — active until ${fmtDate(sub.expires_at || sub.current_period_end, lang)}`}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{cs ? "Plán" : "Plan"}</div>
              <div className="text-zinc-100 font-medium mt-0.5" data-testid="subscription-plan">{sub.plan} — {sub.price_czk?.toFixed(0)} Kč/{cs ? "měs" : "mo"}</div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                {sub.cancel_at_period_end ? (cs ? "Aktivní do" : "Active until") : (cs ? "Příští platba" : "Next payment")}
              </div>
              <div className="text-zinc-100 font-medium mt-0.5" data-testid="subscription-period-end">{fmtDate(sub.current_period_end, lang)}</div>
            </div>
          </div>

          {sub.pending_plan && !sub.cancel_at_period_end && (
            <div className="flex flex-wrap items-center gap-3" data-testid="subscription-pending-plan">
              <span className="text-xs text-amber-400 font-mono">
                {cs ? `Od ${fmtDate(sub.current_period_end, lang)} se plán změní na ${sub.pending_plan} (${sub.pending_plan === "PREMIUM" ? 499 : 1290} Kč/měs).`
                    : `From ${fmtDate(sub.current_period_end, lang)} your plan changes to ${sub.pending_plan}.`}
              </span>
              <button
                onClick={() => act(() => api.post("/billing/subscription/change", { plan: sub.plan }), cs ? "Naplánovaná změna zrušena" : "Scheduled change cancelled")}
                disabled={busy}
                data-testid="subscription-undo-change-btn"
                className="text-xs text-zinc-400 hover:text-zinc-100 underline transition-colors disabled:opacity-50"
              >
                {cs ? "Zrušit změnu" : "Undo change"}
              </button>
            </div>
          )}

          {sub.cancel_at_period_end ? (
            <div className="space-y-3">
              <div className="text-sm text-amber-400" data-testid="subscription-cancelled-note">
                {cs ? `Zrušeno — aktivní do ${fmtDate(sub.current_period_end, lang)}.` : `Cancelled — active until ${fmtDate(sub.current_period_end, lang)}.`}
              </div>
              <button
                onClick={() => act(() => api.post("/billing/subscription/resume"), cs ? "Předplatné obnoveno" : "Subscription resumed")}
                disabled={busy}
                data-testid="subscription-resume-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
              >
                <ArrowsClockwise weight="bold" size={15} /> {busy ? "..." : (cs ? "Obnovit předplatné" : "Resume subscription")}
              </button>
            </div>
          ) : confirm === "cancel" ? (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 p-4 space-y-3" data-testid="subscription-cancel-confirm">
              <p className="text-sm text-zinc-300">
                {cs ? `Opravdu zrušit? Plán ${sub.plan} zůstane aktivní do ${fmtDate(sub.current_period_end, lang)} — o zaplacené dny nepřijdeš. Poté se účet přepne na FREE.`
                    : `Really cancel? Your ${sub.plan} plan stays active until ${fmtDate(sub.current_period_end, lang)} — you keep the days you paid for. Then your account switches to FREE.`}
              </p>
              <div className="flex gap-2">
                <button onClick={() => act(() => api.post("/billing/subscription/cancel"), cs ? "Předplatné zrušeno" : "Subscription cancelled")} disabled={busy} data-testid="subscription-cancel-confirm-btn" className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50">
                  {busy ? "..." : (cs ? "Ano, zrušit" : "Yes, cancel")}
                </button>
                <button onClick={() => setConfirm(null)} disabled={busy} data-testid="subscription-cancel-back-btn" className="px-4 py-2 rounded-md text-sm border border-zinc-800 text-zinc-400 hover:text-zinc-100 transition-colors">
                  {cs ? "Zpět" : "Back"}
                </button>
              </div>
            </div>
          ) : confirm === "change" ? (
            <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-4 space-y-3" data-testid="subscription-change-confirm">
              <p className="text-sm text-zinc-300">
                {isUpgrade
                  ? (cs ? `Přejít na ELITE (1 290 Kč/měs)? Změna platí okamžitě — Stripe doúčtuje poměrnou část rozdílu za zbytek období a vyšší limity máš hned.`
                        : `Upgrade to ELITE (1290 CZK/mo)? The change is immediate — Stripe charges a prorated difference for the rest of the period.`)
                  : (cs ? `Přejít na PREMIUM (499 Kč/měs)? Do ${fmtDate(sub.current_period_end, lang)} zůstává ELITE, od dalšího období se účtuje už jen 499 Kč.`
                        : `Switch to PREMIUM (499 CZK/mo)? ELITE stays until ${fmtDate(sub.current_period_end, lang)}, then you are billed 499 CZK.`)}
              </p>
              <div className="flex gap-2">
                <button onClick={() => act(() => api.post("/billing/subscription/change", { plan: targetPlan }), cs ? (isUpgrade ? "Plán změněn na ELITE" : `Od ${fmtDate(sub.current_period_end, lang)} přejdeš na PREMIUM`) : "Plan changed")} disabled={busy} data-testid="subscription-change-confirm-btn" className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50">
                  {busy ? "..." : (cs ? `Potvrdit změnu na ${targetPlan}` : `Confirm switch to ${targetPlan}`)}
                </button>
                <button onClick={() => setConfirm(null)} disabled={busy} data-testid="subscription-change-back-btn" className="px-4 py-2 rounded-md text-sm border border-zinc-800 text-zinc-400 hover:text-zinc-100 transition-colors">
                  {cs ? "Zpět" : "Back"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {!sub.pending_plan && (
                <button onClick={() => setConfirm("change")} data-testid="subscription-change-btn" className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                  <ArrowsClockwise weight="bold" size={15} /> {cs ? `Přejít na ${targetPlan} (${targetPrice} Kč)` : `Switch to ${targetPlan}`}
                </button>
              )}
              <button onClick={() => setConfirm("cancel")} data-testid="subscription-cancel-btn" className="flex items-center gap-2 px-4 py-2 rounded-md text-sm border border-zinc-800 text-zinc-400 hover:text-red-400 transition-colors">
                <XCircle weight="bold" size={15} /> {cs ? "Zrušit předplatné" : "Cancel subscription"}
              </button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
